import {
  booleanAttribute,
  Directive,
  ElementRef,
  HostListener,
  Renderer2,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';

export type ButtonVariant = 'primary' | 'danger' | 'grey' | 'plain';
export type ButtonSize = 'big' | 'normal' | 'small';
export type ButtonSurface = 'default' | 'muted';

function optionalBooleanAttribute(value: unknown): boolean | undefined {
  return value === undefined ? undefined : booleanAttribute(value);
}

@Directive({
  selector: 'button[cuiButton], a[cuiButton]',
  standalone: true,
  host: {
    class: 'cui-button',
    '[class.cui-button--primary]': 'variant() === "primary"',
    '[class.cui-button--danger]': 'variant() === "danger"',
    '[class.cui-button--grey]': 'variant() === "grey"',
    '[class.cui-button--plain]': 'variant() === "plain"',
    '[class.cui-button--on-muted]': 'surface() === "muted"',
    '[class.cui-button--big]': 'size() === "big"',
    '[class.cui-button--normal]': 'size() === "normal"',
    '[class.cui-button--small]': 'size() === "small"',
    '[class.cui-button--disabled]': 'isDisabled()',
    '[class.cui-button--loading]': 'loading()',
    '[class.cui-button--full-width]': 'fullWidth()',
    '[class.cui-button--square]': 'square()',
    '[class.cui-button--active]': 'active() || pressed() === true',
    '[attr.aria-busy]': 'loading() ? "true" : null',
    '[attr.aria-disabled]': 'isAnchorDisabled() ? "true" : null',
    '[attr.aria-pressed]': 'ariaPressed()',
    '[attr.tabindex]': 'isAnchorDisabled() ? "-1" : null',
  },
})
export class ButtonDirective {
  readonly variant = input<ButtonVariant>('plain');
  readonly size = input<ButtonSize>('normal');
  readonly surface = input<ButtonSurface>('default');
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly loading = input(false, { transform: booleanAttribute });
  readonly fullWidth = input(false, { transform: booleanAttribute });
  readonly square = input(false, { transform: booleanAttribute });
  readonly active = input(false, { transform: booleanAttribute });
  readonly pressed = input<boolean | undefined, unknown>(undefined, {
    transform: optionalBooleanAttribute,
  });

  protected readonly isDisabled = computed(() => this.disabled() || this.loading());
  protected readonly ariaPressed = computed(() => {
    const pressed = this.pressed();

    return pressed === undefined ? null : String(pressed);
  });

  private readonly nativeElement: HTMLAnchorElement | HTMLButtonElement;
  private readonly isNativeButton: boolean;
  private readonly isAnchor: boolean;
  private readonly elementRef =
    inject<ElementRef<HTMLAnchorElement | HTMLButtonElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);

  constructor() {
    this.nativeElement = this.elementRef.nativeElement;
    this.isNativeButton = this.nativeElement.tagName.toLowerCase() === 'button';
    this.isAnchor = this.nativeElement.tagName.toLowerCase() === 'a';

    if (this.isNativeButton && !this.nativeElement.hasAttribute('type')) {
      this.renderer.setAttribute(this.nativeElement, 'type', 'button');
    }

    effect(() => {
      if (!this.isNativeButton) {
        return;
      }

      if (this.isDisabled()) {
        this.renderer.setAttribute(this.nativeElement, 'disabled', '');
        this.renderer.setProperty(this.nativeElement, 'disabled', true);
      } else {
        this.renderer.removeAttribute(this.nativeElement, 'disabled');
        this.renderer.setProperty(this.nativeElement, 'disabled', false);
      }
    });
  }

  protected isAnchorDisabled(): boolean {
    return this.isAnchor && this.isDisabled();
  }

  @HostListener('click', ['$event'])
  protected handleClick(event: Event): void {
    if (!this.isDisabled()) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
  }
}
